import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Grid,
  Typography,
  Divider,
  TextField,
  Tooltip,
  Button,
  makeStyles,
  FormHelperText,
  createMuiTheme,
  ThemeProvider,
} from "@material-ui/core";
import styled from "styled-components";
import Box from "@mui/material/Box";
import Switch from "@mui/material/Switch";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Autocomplete from "@mui/material/Autocomplete";

import draftToHtml from "draftjs-to-html";
import ReactHtmlParser from "react-html-parser";

import TextEditor from "./TextEditor.js";
import DropDown from "./DropDown.js";
import AlertDialog from "../../../components/AlertDialog.js";

import infoImg from "../../../assets/icons/info.png";

const StyledTooltip = styled(props => (
  <Tooltip
    classes={{ popper: props.className }}
    {...props}
    PopperProps={{
      popperOptions: {
        modifiers: {
          flip: { enabled: false },
          offset: {
            offset: "0px, -35px, 20px, -25px",
          },
        },
      },
    }}
  />
))`
  & .MuiTooltip-tooltip {
    display: flex;
    background: #757575;
    color: #fff;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 500;
  }
`;

const formLabelsTheme = createMuiTheme({
  overrides: {
    MuiFormLabel: {
      asterisk: {
        color: "#DB3131",
        "&$error": {
          color: "#DB3131",
        },
      },
    },
  },
});

const useStyles = makeStyles(theme => ({
  outlinedButtonStyle: {
    borderRadius: 4,
    width: "100px",
    padding: 0,
    color: "#0D47A1",
    height: "34px",
    textTransform: "none",
  },
  buttonStyle: {
    borderRadius: 4,
    width: "100px",
    padding: 0,
    color: "#fff",
    height: "34px",
    backgroundColor: "#0D47A1",
    textTransform: "none",
    "&:active": {
      backgroundColor: "#0D47A1",
    },
    "&:hover": {
      backgroundColor: "#1E88E5",
    },
  },
  subDialog: {
    "& .MuiDialogContent-root": {
      padding: 0,
    },
    "& .MuiPaper-root": {
      width: "100%",
      maxWidth: "1100px",
      height: "600px",
    },
  },
  textFieldStyle: {
    marginBottom: 5,
    marginTop: 20,
    "& .MuiInputLabel-outlined": {
      fontSize: 16,
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: 4,
      fontSize: 16,
      backgroundColor: "#fff",
    },
  },
  textFieldOnFormStyle: {
    marginBottom: 5,
    "& .MuiInputLabel-outlined": {
      fontSize: 16,
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      fontSize: 16,
      backgroundColor: "#fff",
    },
  },

  switch: {
    "& .MuiSwitch-switchBase.Mui-checked+.MuiSwitch-track": {
      backgroundColor: "#0D47A1",
    },
    "& .MuiSwitch-switchBase.Mui-checked": {
      color: "#0D47A1",
    },
  },
  errorStyle: {
    marginTop: 0,
    fontSize: 14, //16,
    fontFamily: "Arial",
  },
}));

export default function AddDropDownToFormDialog({ closeDialog, handleDone }) {
  const classes = useStyles();
  const [open, setOpen] = useState(true);
  const [infoIcon, setInfoIcon] = useState(false);
  const [manAnswer, setManAnswer] = useState(false);
  const [queText, setQueText] = useState(false);
  const [queRaw, setQueRaw] = useState(false);
  const [capText, setCapText] = useState(false);
  const [capRaw, setCapRaw] = useState(false);
  const [maxChar, setMaxChar] = useState("");
  const [infoTxt, setInfoTxt] = useState("");
  const [alertMsg, setAlertMsg] = useState([]);
  const [selectedVal, setSelectedVal] = useState("Select");
  const [selectedOpts, setSelectedOpts] = useState([]);
  const [value, setValue] = useState(null);

  let tooltipInput = useRef(null);
  const editorRef = useRef(null);
  const [checkError, setCheckError] = useState(false);

  useEffect(() => {
    if (infoIcon) {
      tooltipInput.current.focus();
    }
  }, [infoIcon]);

  const handleClose = () => {
    setOpen(false);
    closeDialog && closeDialog();
  };

  const handleChangeValue = (event, value) => {
    setValue(value);
  };

  const handleChangeTextEditor = (txt, type) => {
    let contentHTML = draftToHtml(txt);
    contentHTML = contentHTML.replace(/<p>/, "");

    contentHTML = contentHTML.replace(
      /<a[^>]*href="([^"]+)"[^>]*>(.*?)<\/a>/gi,
      (match, href, text) => {
        // Remove leading protocols
        href = href.replace(/^(https?:\/\/)+/, "");

        // Prepend https://
        href = `https://${href}`;

        return `<a href="${href}" target="_blank">${text}</a>`;
      }
    );

    const convertedTxt = ReactHtmlParser(contentHTML);

    if (type === "question") {
      setQueText(convertedTxt);
      setQueRaw(txt);
    }
    if (type === "caption") {
      setCapText(convertedTxt);
      setCapRaw(txt);
    }
  };

  const handleChangeSelectValue = (val, options) => {
    setSelectedVal(val);
    setSelectedOpts(options);
  };

  const handleRemoveFromList = option => {
    value && value.title === option.title && setValue(null);
  };

  const validateFields = () => {
    setCheckError(true);
    if (queRaw.blocks[0].text.trim() === "") {
      editorRef.current.focus();
      return false;
    }
    if (infoIcon && infoTxt.trim() === "") {
      return false;
    }
    return true;
  };

  return (
    <Dialog
      open={open}
      fullWidth={true}
      maxWidth="xl"
      //  onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className={classes.subDialog}
    >
      <DialogTitle sx={{ p: 0 }} id="alert-dialog-title">
        <Box
          style={{
            display: "flex",
            flexDirection: "row",
          }}
        >
          <Grid container>
            <Grid
              item
              lg={4}
              xl={4}
              md={4}
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                paddingBottom: 15,
              }}
            >
              <Box style={{ width: 100 }}>
                <IconButton
                  aria-label="close"
                  sx={{
                    position: "absolute",
                    left: 8,
                    top: 8,
                    align: "right",
                    color: theme => theme.palette.grey[500],
                  }}
                  onClick={e => {
                    if (
                      queRaw.blocks[0].text ||
                      capRaw.blocks[0].text ||
                      selectedVal !== "Select" ||
                      infoTxt
                    )
                      setAlertMsg({
                        title: "Exit",
                        type: "saveConfirmation",
                        msg: "Are you sure you want to exit without saving your work?",
                      });
                    else handleClose();
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
              <Box pt={2} style={{ width: 350 }}>
                <Typography
                  variant="h1"
                  style={{
                    fontSize: 20,
                    display: "flex",
                    //    flexDirection: "end",
                    alignItems: "center",
                  }}
                >
                  Object: Drop Down
                </Typography>
              </Box>
            </Grid>
            <Grid item lg={8} xl={8} md={8} style={{ backgroundColor: "#E3F2FD" }}></Grid>
          </Grid>
        </Box>
      </DialogTitle>
      <DialogContent
        style={{
          alignItems: "center",
          display: "flex",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        <Grid container>
          <Grid item lg={4} xl={4} md={4}>
            <Card style={{ maxHeight: "550px", overflowY: "scroll" }}>
              <Box mb={1} mt={5}>
                <Divider />
                <Box mt={2} ml={2}>
                  <Typography variant="h3">Question*</Typography>
                </Box>
                <Box mt={2} ml={2} mb={2} mr={2}>
                  <TextEditor
                    type="question"
                    handleChangeText={(txt, type) => handleChangeTextEditor(txt, type)}
                    editorRef={editorRef}
                  />
                  {checkError && queRaw && queRaw.blocks[0].text.trim() === "" && (
                    <FormHelperText className={classes.errorStyle} error>
                      Question field cannot be blank.
                    </FormHelperText>
                  )}
                </Box>
                <Divider />
                <Box mt={2} ml={2}>
                  <Typography variant="h3">Caption</Typography>
                </Box>
                <Box mt={2} ml={2} mb={2} mr={2}>
                  <TextEditor
                    type="caption"
                    handleChangeText={(txt, type) => handleChangeTextEditor(txt, type)}
                  />
                </Box>
                <Divider />
                <Box mt={2} ml={2}>
                  <Typography variant="h3">Option</Typography>
                </Box>
                <Box mt={2} ml={2} mb={2} mr={2}>
                  <DropDown
                    handleChangeSelect={(option, values) =>
                      handleChangeSelectValue(option, values)
                    }
                    setAutoCompleteValue={val => setValue(val)}
                    removeFromAutoComplete={option => handleRemoveFromList(option)}
                    passEditedValue={(newVal, oldVal) =>
                      value && value.title === oldVal && setValue(newVal)
                    }
                  />
                </Box>
                <Divider />
                <Box
                  mt={2}
                  ml={2}
                  mr={1}
                  mb={2}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="h3">Answer is Mandatory</Typography>
                  <Switch
                    checked={manAnswer}
                    onChange={() => setManAnswer(!manAnswer)}
                    className={classes.switch}
                  />
                </Box>
                <Divider />
                <Box
                  mt={2}
                  ml={2}
                  mr={1}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="h3">Enable Information Icon</Typography>
                  <Switch
                    checked={infoIcon}
                    onChange={() => {
                      setInfoIcon(!infoIcon);
                      infoIcon && setInfoTxt("");
                    }}
                    className={classes.switch}
                  />
                </Box>
                <Box ml={2} mb={2} mr={2}>
                  <TextField
                    inputRef={tooltipInput}
                    id="outlined-multiline-static"
                    variant="outlined"
                    placeholder="Type tooltip label..."
                    value={infoTxt}
                    disabled={!infoIcon}
                    onChange={e => setInfoTxt(e.target.value)}
                    className={classes.textFieldStyle}
                    fullWidth
                    helperText={
                      checkError &&
                      infoIcon &&
                      infoTxt.trim() === "" &&
                      "This field cannot be blank."
                    }
                    error={checkError && infoIcon && infoTxt.trim() === ""}
                  />
                </Box>
                <Box
                  mt={5}
                  ml={2}
                  //  mb={3}
                  style={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    className={classes.buttonStyle}
                    style={{ marginRight: 2 }}
                    onClick={() => {
                      if (!validateFields()) return;
                      if (queRaw.blocks[0].text || capRaw.blocks[0].text || infoTxt)
                        handleDone(
                          "Drop Down",
                          "done",
                          infoIcon,
                          manAnswer,
                          queText,
                          queRaw,
                          capText,
                          capRaw,
                          maxChar,
                          infoTxt,
                          selectedVal,
                          selectedOpts
                        );
                      else {
                        setAlertMsg({
                          title: "Alert",
                          msg: "Nothing is added.",
                        });
                      }
                    }}
                  >
                    Save
                  </Button>
                  <Button
                    variant="text"
                    color="primary"
                    className={classes.outlinedButtonStyle}
                    onClick={handleClose}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            </Card>
          </Grid>
          <Grid item lg={8} md={8} xl={8}>
            <Card style={{ backgroundColor: "#E3F2FD" }}>
              <Box
                mt={6}
                mb={1.5}
                ml={10}
                mr={10}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Box
                  style={{
                    display: "flex",
                    flexDirection: "row",
                  }}
                >
                  <Box style={{ display: "flex", flexDirection: "row" }}>
                    {infoIcon && infoTxt ? (
                      <StyledTooltip title={infoTxt}>
                        <img
                          style={{
                            paddingBottom: 3,
                            paddingRight: 5,
                            height: 24,
                          }}
                          src={infoImg}
                        />
                      </StyledTooltip>
                    ) : infoIcon && !infoTxt ? (
                      <img
                        style={{
                          paddingBottom: 3,
                          paddingRight: 5,
                          height: 24,
                        }}
                        src={infoImg}
                      />
                    ) : (
                      ""
                    )}
                  </Box>
                  <Typography
                    variant="h3"
                    style={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        wordBreak: "break-word",
                      }}
                    >
                      {queText ? queText : ""}
                    </div>
                  </Typography>
                </Box>
                <Typography
                  variant="h3"
                  style={{
                    fontSize: 14,
                    display: "flex",
                    alignItems: "center",
                    paddingLeft: 3,
                    paddingBottom: 5,
                    paddingTop: 20,
                  }}
                >
                  <div
                    style={{
                      wordBreak: "break-word",
                    }}
                  >
                    {capText ? capText : ""}
                  </div>
                </Typography>
                <ThemeProvider theme={formLabelsTheme}>
                  <form noValidate autoComplete="off">
                    <Autocomplete
                      key={selectedVal}
                      options={selectedOpts}
                      disableClearable
                      fullWidth
                      value={value}
                      onChange={handleChangeValue}
                      getOptionLabel={option => option.title}
                      renderInput={params => (
                        <TextField
                          required={manAnswer}
                          fullWidth
                          label="Select"
                          margin="normal"
                          name="selectOptions"
                          id="selectOptions"
                          variant="outlined"
                          {...params}
                        />
                      )}
                    />
                  </form>
                </ThemeProvider>
              </Box>
            </Card>
          </Grid>
          {alertMsg.msg && (
            <AlertDialog
              title={alertMsg.title}
              type={alertMsg.type}
              contentMsg={alertMsg.msg}
              closeDialog={() => setAlertMsg([])}
              cancelSaveAction={() => {
                setAlertMsg([]);
                handleClose();
              }}
            />
          )}
        </Grid>
      </DialogContent>
    </Dialog>
  );
}
